import { expValEquals } from '@confluence/feature-experiments';

import {
	getFloatingBanners,
	hideFloatingBanner,
	seedFloatingBanners,
	showFloatingBanner,
	subscribeToFloatingBanners,
} from './floatingBannerStore.ts';
import type { BannerStateInfo } from './BannerStateContainer.ts';
import { findBanner } from './BannerStateContainer.ts';
import { BaseStateContainer } from './BaseStateContainer.ts';

type FloatingBannerStateContainerState = {
	floatingBanners: BannerStateInfo[];
};

export class FloatingBannerStateContainer extends BaseStateContainer<FloatingBannerStateContainerState> {
	state: FloatingBannerStateContainerState = {
		floatingBanners: [],
	};

	// Capture the backing at construction so writes and reads cannot diverge after a flag change.
	private readonly storeBacked: boolean;
	private unsubscribeFromStore: (() => void) | null = null;
	private pendingNotify: Promise<void> | null = null;

	constructor(props: { initialState?: FloatingBannerStateContainerState } = {}) {
		super();
		this.state = props.initialState ?? { floatingBanners: [] };
		this.storeBacked = expValEquals('confluence_banner_store_migration', 'isEnabled', true);
		if (this.storeBacked) {
			seedFloatingBanners(this.state.floatingBanners);
			this.state = { floatingBanners: getFloatingBanners() };
			this.unsubscribeFromStore = subscribeToFloatingBanners(() => {
				this.state = { floatingBanners: getFloatingBanners() };
				this.scheduleNotify();
			});
		}
	}

	showFloating = (name: string, height: number): void => {
		if (this.storeBacked) {
			showFloatingBanner(name, height);
			return;
		}

		// The lookup has to happen inside the updater: updates queued in the same microtask are
		// applied in sequence, so an index resolved beforehand would already be stale.
		void this.setState((state) => {
			const index = findBanner(name, state.floatingBanners);

			if (index === -1) {
				return { ...state, floatingBanners: [...state.floatingBanners, { name, height }] };
			}

			if (state.floatingBanners[index].height !== height) {
				const newArray = [...state.floatingBanners];
				// Replace rather than mutate, so the previous state keeps the old height.
				newArray[index] = { name, height };
				return { ...state, floatingBanners: newArray };
			}

			return undefined;
		});
	};

	hideFloating = (name: string): void => {
		if (this.storeBacked) {
			hideFloatingBanner(name);
			return;
		}

		void this.setState((state) => {
			const index = findBanner(name, state.floatingBanners);

			if (index > -1) {
				const newArray = [...state.floatingBanners];
				newArray.splice(index, 1);
				return { ...state, floatingBanners: newArray };
			}

			return undefined;
		});
	};

	getFloatingHeightBefore = (name: string): number => {
		const index = findBanner(name, this.state.floatingBanners);
		if (index === -1) {
			return 0;
		}

		return this.state.floatingBanners
			.slice(0, index)
			.reduce((height: number, banner: BannerStateInfo) => {
				return height + banner.height;
			}, 0);
	};

	getFloatingHeight = (): number =>
		this.state.floatingBanners.reduce(
			(height: number, banner: BannerStateInfo) => height + banner.height,
			0,
		);

	/** Short-lived legacy adapters must detach; hook consumers do not create an adapter. */
	destroy = (): void => {
		this.unsubscribeFromStore?.();
		this.unsubscribeFromStore = null;
	};

	private scheduleNotify = (): void => {
		if (this.pendingNotify) return;
		this.pendingNotify = Promise.resolve().then(() => {
			this.pendingNotify = null;
			return this.notifyListeners();
		});
	};
}
